import { useAuth, useSignIn } from "@clerk/react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { PasswordField } from "@/components/auth/password-field";
import { PrimaryButton } from "@/components/auth/primary-button";
import { TextButton, TextLink } from "@/components/auth/text-link";
import { authPath, hasEnterpriseSsoFactor } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import { finishSignIn } from "@/lib/finish-sign-in";

const SignInPasswordPage = () => {
  const navigate = useNavigate();
  const { isLoaded } = useAuth();
  const { fetchStatus, signIn } = useSignIn();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const email = signIn.identifier ?? "";

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    if (signIn.identifier && signIn.status === "needs_first_factor") {
      return;
    }
    navigate(authPath("/login", { email: signIn.identifier ?? undefined }), {
      replace: true,
    });
  }, [isLoaded, navigate, signIn.identifier, signIn.status]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password) {
      setError("Enter your password to continue.");
      return;
    }
    setError(null);
    setPending(true);
    const { error: passwordError } = await signIn.password({ password });
    if (passwordError) {
      setError(clerkFailureMessage(passwordError));
      setPending(false);
      return;
    }
    if (
      signIn.status === "needs_first_factor" &&
      hasEnterpriseSsoFactor(signIn.supportedFirstFactors)
    ) {
      const { error: ssoError } = await signIn.sso({
        identifier: email,
        redirectCallbackUrl: "/sso-callback",
        redirectUrl: "/sso-callback",
        strategy: "enterprise_sso",
      });
      setError(clerkFailureMessage(ssoError));
      setPending(false);
      return;
    }
    const message = await finishSignIn(signIn, navigate);
    setError(message);
    setPending(false);
  };

  return (
    <AuthScreen
      subtitle={
        <span className="flex flex-wrap items-baseline gap-3">
          <span>{email}</span>
          <TextButton
            onClick={() => {
              const current = signIn.identifier ?? "";
              signIn.reset().finally(() => {
                navigate(authPath("/login", { email: current }));
              });
            }}
          >
            Change
          </TextButton>
        </span>
      }
      title="Enter your password"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <PasswordField
          aside={
            <TextLink to={authPath("/login/reset")}>Forgot password?</TextLink>
          }
          autoComplete="current-password"
          error={error}
          id="password"
          label="Password"
          onChange={setPassword}
          value={password}
        />
        <PrimaryButton
          busy={pending || fetchStatus === "fetching"}
          type="submit"
        >
          Sign in
        </PrimaryButton>
      </form>
    </AuthScreen>
  );
};

export default SignInPasswordPage;
